import { generateId } from '@/lib/utils.util';
import type {
  DataSource,
  Message,
  Phase,
  SelectedSources,
  ViewPreferences,
} from '@/types/builder.types';

import type { StateCreator } from 'zustand';

export interface BuilderSlice {
  // State
  messages: Message[];
  currentInput: string;
  phase: Phase;
  selectedSources: SelectedSources;
  viewPreferences: ViewPreferences;

  // Actions
  addMessage: (
    sender: Message['sender'],
    content: string,
    type?: Message['type'],
    data?: Message['data']
  ) => void;
  setCurrentInput: (input: string) => void;
  setPhase: (phase: Phase) => void;
  toggleSourceSelection: (sourceId: string) => void;
  setSourceSelection: (sourceId: string, selected: boolean) => void;
  toggleViewPreference: (category: keyof ViewPreferences, itemId: string) => void;
  resetBuilder: () => void;
  getSelectedSourceCount: () => number;
  getTotalEstimatedCost: () => number;
}

const initialBuilderState = {
  messages: [],
  currentInput: '',
  phase: 'initial' as Phase,
  selectedSources: {},
  viewPreferences: {},
};

export const createBuilderSlice: StateCreator<BuilderSlice, [], [], BuilderSlice> = (set, get) => ({
  ...initialBuilderState,

  addMessage: (sender, content, type = 'text', data) => {
    if (get().messages.some((message) => message.content === content)) {
      return;
    }
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: generateId(),
          sender,
          content,
          type,
          data,
          timestamp: new Date(),
        },
      ],
    }));
  },

  setCurrentInput: (input) => {
    set({ currentInput: input });
  },

  setPhase: (phase) => {
    set({ phase });
  },

  toggleSourceSelection: (sourceId) => {
    set((state) => ({
      selectedSources: {
        ...state.selectedSources,
        [sourceId]: !state.selectedSources[sourceId],
      },
    }));
  },

  setSourceSelection: (sourceId, selected) => {
    set((state) => ({
      selectedSources: {
        ...state.selectedSources,
        [sourceId]: selected,
      },
    }));
  },

  toggleViewPreference: (category, itemId) => {
    set((state) => {
      const currentPrefs = state.viewPreferences[category] || [];
      const isSelected = currentPrefs.includes(itemId);

      return {
        viewPreferences: {
          ...state.viewPreferences,
          [category]: isSelected
            ? currentPrefs.filter((id) => id !== itemId)
            : [...currentPrefs, itemId],
        },
      };
    });
  },

  resetBuilder: () => {
    set(initialBuilderState);
  },

  getSelectedSourceCount: () => {
    return Object.values(get().selectedSources).filter(Boolean).length;
  },

  getTotalEstimatedCost: () => {
    const { messages, selectedSources } = get();
    const dataSourcesMessage = messages.find((m) => m.type === 'data-sources');

    if (!dataSourcesMessage || !dataSourcesMessage.data) {
      return 0;
    }

    const dataSources = dataSourcesMessage.data as DataSource[];
    return Object.keys(selectedSources)
      .filter((id) => selectedSources[id])
      .reduce((sum, id) => {
        const source = dataSources.find((s) => s.id === id);
        return sum + (source?.pricing.estimated || 0);
      }, 0);
  },
});
